import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';

/**
 * PremiumFeatureModal - Popup version of the premium upsell card.
 * Shares the same visual design as PremiumFeatureGate but renders as a
 * floating overlay rather than a full-page wrapper, so it can be used
 * inside modals, wizards, and other inline contexts.
 *
 * @param {boolean}  props.show        - Whether the modal is visible
 * @param {Function} props.onHide      - Called when the user dismisses it
 * @param {string}   props.featureName - Feature name shown in the description
 */
const PremiumFeatureModal = ({ show, onHide, featureName = 'This feature' }) => {
  const navigate = useNavigate();

  if (!show) return null;

  // Render into document.body via a portal so it escapes any parent
  // stacking context (e.g. a Bootstrap Modal that already has z-index 1050).
  return ReactDOM.createPortal(
    <div style={styles.overlay} onClick={onHide}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Gold top accent bar */}
        <div style={styles.topAccent} />

        {/* Star icon */}
        <div style={styles.iconContainer}>
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="url(#premiumGradientModal)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <defs>
              <linearGradient id="premiumGradientModal" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>

        <h2 style={styles.title}>Premium Feature</h2>

        <p style={styles.description}>
          <strong style={styles.featureName}>{featureName}</strong> is a premium feature.
          Upgrade your account to unlock this and other powerful capabilities!
        </p>

        <button
          onClick={() => { onHide(); navigate('/pricing'); }}
          style={styles.ctaButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(251, 191, 36, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 14px rgba(251, 191, 36, 0.4)';
          }}
        >
          Try Premium for Free
        </button>

        <button
          onClick={onHide}
          style={styles.backButton}
          onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
        >
          Maybe Later
        </button>

        <p style={styles.infoText}>Premium includes unlimited access to all features.</p>
      </div>
    </div>,
    document.body
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10, 15, 44, 0.75)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1060, // above Bootstrap modals (z-index 1050)
    padding: '20px',
  },
  card: {
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(30, 35, 60, 0.98), rgba(20, 25, 50, 0.98))',
    borderRadius: '24px',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(251, 191, 36, 0.1)',
    padding: '48px 40px',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
  },
  topAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24)',
  },
  iconContainer: {
    marginBottom: '20px',
  },
  title: {
    color: '#ffffff',
    fontSize: '26px',
    fontWeight: '700',
    marginBottom: '16px',
    fontFamily: "'Helvetica', sans-serif",
  },
  description: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '28px',
  },
  featureName: {
    color: '#fbbf24',
  },
  ctaButton: {
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#1a1a2e',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 14px rgba(251, 191, 36, 0.4)',
    marginBottom: '12px',
  },
  backButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '500',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  infoText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '13px',
    marginTop: '20px',
    marginBottom: 0,
  },
};

export default PremiumFeatureModal;
